import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router'
import { doc, getDoc, getFirestore } from 'firebase/firestore'
import { FirebaseService } from '../services/firebase.service'
import { LocalStorageService } from '../services/local-storage-service.service'
import { DelphireLinkService } from '../services/delphire-link.service'
import { environment } from '../../environments/environment'
import { Space } from '../interfaces/space'
import { Widget } from '../interfaces/widget'
import moment from 'moment'

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnInit {
  @Input()
  widget: Widget = {
    h: 0,
    i: '',
    id: '',
    minH: 0,
    minW: 0,
    moved: false,
    static: false,
    w: 0,
    x: 0,
    y: 0,
    position: {
      x: 0,
      y: 0
    },
    size: {
      width: 0,
      height: 0
    },
    style: {},
    type: '',
    roadmapTitle: undefined,
    description: '',
    disabled: false
  }

  Object = Object;
  calendar: any;
  events: any;
  calendarInterval: any;
  nearestDateIndex: any;

  constructor(
    private localStorage: LocalStorageService,
    private fbS: FirebaseService,
    private linkService: DelphireLinkService,
    private route: ActivatedRoute
  ) { }

  // ------------------------------------------------------------------------------------

  getHeadline(): string {
    if(this.calendar && this.calendar.displayName.trim() != ''){
      return this.calendar.displayName
    }
    if(this.widget.label && this.widget.label.trim() != ''){
      return this.widget.label
    }
    return "Upcoming Events"
  }

  // ------------------------------------------------------------------------------------

  getEvents(): any {
    return this.calendar.events
  }

  getMonth(calendarEvent: any): any {
    let date = moment(calendarEvent.date)
    return date.format('MMMM')
  }

  getDay(calendarEvent: any): any {
    let date = moment(calendarEvent.date)
    return date.format('DD')
  }

  // ------------------------------------------------------------------------------------

  getBackgroundColor(): string {
    if (this.widget.style && this.widget.style.backgroundColor) {
      if (this.widget.style.opacity) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
          this.widget.style.backgroundColor
        )
        if (result) {
          return (
            'rgba(' +
            parseInt(result[1], 16) +
            ', ' +
            parseInt(result[2], 16) +
            ', ' +
            parseInt(result[3], 16) +
            (', ' + this.widget.style.opacity + ')')
          )
        } else {
          return this.widget.style.backgroundColor
        }
      } else {
        return this.widget.style.backgroundColor
      }
    } else {
      return '#d2d9e0'
    }
  }

  hasIcon(): boolean {
    return this.widget.iconResourceId ? true : false
  }

  getIcon(): string {
    if (this.widget.iconResourceId) {
      if (this.widget.iconResourceId.indexOf('icon') > -1) {
        return 'da-2x ' + this.widget.iconResourceId
      } else {
        return 'fa fa-2x fa-' + this.widget.iconResourceId
      }
    } else {
      return 'none'
    }
  }

  getIconColor(): string {
    if (this.widget.style && this.widget.style.iconColor) {
      return this.widget.style.iconColor
    } else {
      return '#5875BB'
    }
  }

  getTextColor(): string {
    if (this.widget.style && this.widget.style.textColor) {
      return this.widget.style.textColor
    } else {
      return '#2A2A2A'
    }
  }

  getDescriptionColor(): string {
    if (this.widget.style && this.widget.style.descriptionColor) {
      return this.widget.style.descriptionColor
    } else {
      return '#727272'
    }
  }

  // ------------------------------------------------------------------------------------

  async clickItem(ev: any) {
    console.log('%c clickItem', 'background: green', ev);
    switch(ev.linkEvent) {
      case 'DO_NOT_LINK':
        console.log('%c clickItem: ', 'background: orange', 'DO_NOT_LINK');
        break;
      case 'URL':
        console.log('%c clickItem: URL =', 'background: orange', ev.url);
        window.open(ev.url, '_blank');
        break;
      case 'COURSE':
        console.log('%c clickItem: COURSE =', 'background: orange', ev.courseId);
        if (ev.courseId) {
          this.linkService.handleLink({
            type: 'course',
            id: ev.courseId,
            route: this.route
          })
        }
        break;
    }
  }

  // ------------------------------------------------------------------------------------

  fetchCalendarData(): void {
    const { databaseUrl } = this.localStorage.get('currentSpace')
    this.fbS
      .db(databaseUrl)
      .object(environment.firebasePaths.space + 'calendars/' + this.widget.parameters?.calendarId)
      .subscribe((calendar) => {
        console.log('%c calendar', 'background: red', calendar)
        if(calendar) {
          // sort calendar events by date
          const calArray = Object.values(calendar.events)
          calArray.sort((a: any, b: any) => {
            return new Date(a.date).getTime() - new Date(b.date).getTime()
          })
          calendar.events = calArray
          console.log('%c calArray', 'background: red', calArray)
          this.calendar = calendar
          this.events = calArray
          console.log('%c this.calendar', 'background: green', this.calendar)
          this.scrollToNearestEvent()
        }
      })
  }

  // ------------------------------------------------------------------------------------

  scrollToNearestEvent() {
    // loop through this.events and push all dates to an array called dates
    console.log("%c this.events", 'background: #00f; color: #fff;', this.events);
    const dates = this.events.map((event: any) => {
      return event.date
    })
    // const dates = ["January 1st", "March 28th", "April 12th", "April 21st", "May 2nd", "September 8th"];
    const nearestDate = this.findNearestDate(dates); // moment object
    console.log("%c Nearest date to now:", 'background: #00f; color: #fff;', nearestDate.format("YYYY-MM-DD"));

    // find the index of the nearest date in this.events
    this.nearestDateIndex = this.events.findIndex((event: any) => {
      return moment(event.date).isSame(nearestDate, 'day')
    }
    )
    console.log("%c this.nearestDateIndex:", 'background: #00f; color: #fff;', this.nearestDateIndex);


    // scroll to the nearest date once the dom shows it
    this.calendarInterval = setInterval(() => {

      const nearestEvent = this.events[this.nearestDateIndex];
      const nearestEventElement = document.getElementById("calendar-item-" + this.calendar.id + "-" + this.nearestDateIndex);
      const contentElement = document.getElementById("calendar-content-" + this.calendar.id);

      console.log("%c --------", 'background: #00f; color: #fff;');
      console.log("%c nearestEvent:", 'background: #00f; color: #fff;', nearestEvent);
      console.log("%c str:", 'background: #00f; color: #fff;', "calendar-item-" + this.calendar.id + "-" + this.nearestDateIndex);
      console.log("%c nearestEventElement:", 'background: #00f; color: #fff;', nearestEventElement);

      if (contentElement && nearestEventElement) {
        contentElement?.scrollTo({
          top: nearestEventElement.offsetTop - contentElement.offsetTop,
          behavior: 'smooth'
        })
        clearInterval(this.calendarInterval)
      }
    }, 250);

  }

  // ------------------------------------------------------------------------------------

  findNearestDate(dates:any) {
    const now = moment();
    const nearestDate = dates.reduce((nearest:any, dateStr:any) => {
      const momentDate = moment(dateStr); // if dates are in a weird format, set the format here
      const timeFromNow = Math.abs(Number(momentDate) - Number(now));
      const nearestTimeFromNow = Math.abs(Number(nearest) - Number(now));
      if (!nearestTimeFromNow || timeFromNow < nearestTimeFromNow) {
        nearest = momentDate;
      }
      return nearest;
    })
    return moment(nearestDate);
  }

  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    console.log('%c this.widget', 'background: red', this.widget)
    console.log('%c this.widget.parameters.calendarId', 'background: red', this.widget.parameters?.calendarId)
    this.fetchCalendarData();
  }
}
